import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Zap, Check } from "lucide-react";

export const SUBSCRIPTION = { key: "sub_monthly", label: "Abbonamento Pro", chars: 90000, price: "€9,99", period: "/ mese" };
export const TOPUP = { key: "topup_15k", label: "Pacchetto extra", chars: 15000, price: "€2,99" };

export default function BuyCreditsModal({ open, onOpenChange, reason }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const subscribed = user?.sub_status === "active";

  const buy = async (key) => {
    setLoading(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setLoading(null);
    }
  };

  const fmt = (n) => n.toLocaleString("it-IT");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-border/60 bg-card overflow-hidden" data-testid="buy-credits-modal">
        <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand-pink/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-brand-blue/20 blur-3xl pointer-events-none" />
        <DialogHeader className="relative">
          <div className="mx-auto h-12 w-12 rounded-2xl tracing-beam flex items-center justify-center mb-2">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <DialogTitle className="text-center font-heading text-2xl font-medium">
            {subscribed ? "Aggiungi " : "Sblocca le "}<span className="text-brand-pink">battute</span>
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-muted-foreground">
            {reason || (subscribed
              ? "Hai l'abbonamento attivo. Aggiungi un pacchetto extra quando le battute del mese non bastano."
              : "Abbonati per 90.000 battute al mese, oppure acquista pacchetti extra all'occorrenza.")}
          </DialogDescription>
        </DialogHeader>

        <div className="relative space-y-3 my-2">
          {!subscribed && (
            <div data-testid="option-subscription" className="rounded-2xl border border-brand-pink/50 bg-brand-pink/5 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-heading text-lg font-semibold">{SUBSCRIPTION.label}</p>
                    <span className="text-[10px] font-mono-label uppercase tracking-wider bg-brand-pink/15 text-brand-pink px-2 py-0.5 rounded-full flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" /> Mensile
                    </span>
                  </div>
                  <p className="text-sm text-brand-blue font-medium">{fmt(SUBSCRIPTION.chars)} battute / mese</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Si rinnova ogni mese. Disdici quando vuoi.</p>
                </div>
                <Button data-testid="buy-sub_monthly" onClick={() => buy(SUBSCRIPTION.key)} disabled={loading !== null}
                  className="rounded-full h-10 px-5 shrink-0 bg-brand-pink hover:bg-brand-pink/90 text-white">
                  {loading === SUBSCRIPTION.key ? <Loader2 className="h-4 w-4 animate-spin" /> : `${SUBSCRIPTION.price}${SUBSCRIPTION.period}`}
                </Button>
              </div>
            </div>
          )}

          <div data-testid="option-topup" className="rounded-2xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-heading text-lg font-semibold">{TOPUP.label}</p>
                  <span className="text-[10px] font-mono-label uppercase tracking-wider bg-brand-blue/15 text-brand-blue px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Zap className="h-3 w-3" /> Una tantum
                  </span>
                </div>
                <p className="text-sm text-brand-blue font-medium">{fmt(TOPUP.chars)} battute</p>
                <p className="text-xs text-muted-foreground mt-0.5">Non scadono. Perfette quando finisci il mese.</p>
              </div>
              <Button data-testid="buy-topup_15k" onClick={() => buy(TOPUP.key)} disabled={loading !== null}
                className="rounded-full h-10 px-5 shrink-0 bg-brand-blue hover:bg-brand-blue/90 text-white">
                {loading === TOPUP.key ? <Loader2 className="h-4 w-4 animate-spin" /> : TOPUP.price}
              </Button>
            </div>
          </div>
        </div>

        <p className="relative text-center text-[11px] text-muted-foreground flex items-center justify-center gap-1.5">
          <Check className="h-3 w-3 text-brand-blue" /> Pagamento sicuro Stripe · carta di test 4242 4242 4242 4242
        </p>
      </DialogContent>
    </Dialog>
  );
}
